import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle, Lock, Eye, EyeOff, Shield } from 'lucide-react';
import api from '../../services/api';

const SetPassword: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | null>(null);

    useEffect(() => {
        if (!token) {
            setError('Token de vérification manquant');
            return;
        }
    }, [token]);

    const checkPasswordStrength = (password: string): 'weak' | 'medium' | 'strong' => {
        if (password.length < 8) return 'weak';

        let score = 0;
        if (password.length >= 8) score++;
        if (password.length >= 12) score++;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;

        if (score <= 2) return 'weak';
        if (score <= 3) return 'medium';
        return 'strong';
    };

    const handlePasswordChange = (password: string) => {
        setFormData(prev => ({ ...prev, password }));
        setPasswordStrength(checkPasswordStrength(password));
    };

    const getPasswordStrengthColor = () => {
        switch (passwordStrength) {
            case 'weak': return 'text-red-600';
            case 'medium': return 'text-yellow-600';
            case 'strong': return 'text-green-600';
            default: return 'text-gray-400';
        }
    };

    const getPasswordStrengthText = () => {
        switch (passwordStrength) {
            case 'weak': return 'Faible';
            case 'medium': return 'Moyen';
            case 'strong': return 'Fort';
            default: return '';
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validation
        if (formData.password.length < 8) {
            setError('Le mot de passe doit contenir au moins 8 caractères');
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Les mots de passe ne correspondent pas');
            return;
        }

        if (passwordStrength === 'weak') {
            setError('Veuillez choisir un mot de passe plus fort');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const response = await api.post('/auth/set-password', {
                token,
                password: formData.password,
                confirm_password: formData.confirmPassword
            });

            if (response.data) {
                setSuccess(true);
                // Redirect to login page after 3 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            }
        } catch (err: any) {
            console.error('Password setting error:', err);
            setError(
                err.response?.data?.detail ||
                'Une erreur est survenue lors de la définition de votre mot de passe'
            );
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardContent className="flex flex-col items-center space-y-4 p-6">
                        <CheckCircle className="h-12 w-12 text-green-600" />
                        <div className="text-center space-y-2">
                            <h2 className="text-xl font-semibold text-gray-900">
                                Mot de passe défini avec succès!
                            </h2>
                            <p className="text-gray-600">
                                Votre compte est maintenant activé. Vous allez être redirigé vers la page de connexion...
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <div className="mx-auto mb-4">
                        <Shield className="h-12 w-12 text-emerald-600" />
                    </div>
                    <CardTitle className="text-2xl">Créer votre mot de passe</CardTitle>
                    <p className="text-gray-600">
                        Choisissez un mot de passe sécurisé pour votre compte
                    </p>
                </CardHeader>
                <CardContent>
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <XCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="password">Mot de passe</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={(e) => handlePasswordChange(e.target.value)}
                                    placeholder="Entrez votre mot de passe"
                                    className="pr-10"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>

                            {formData.password && (
                                <div className="space-y-1">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-600">Force du mot de passe:</span>
                                        <span className={`font-medium ${getPasswordStrengthColor()}`}>
                                            {getPasswordStrengthText()}
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className={`h-2 rounded-full transition-all duration-300 ${passwordStrength === 'weak' ? 'bg-red-500 w-1/3' :
                                                    passwordStrength === 'medium' ? 'bg-yellow-500 w-2/3' :
                                                        passwordStrength === 'strong' ? 'bg-green-500 w-full' : ''
                                                }`}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                            <div className="relative">
                                <Input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={formData.confirmPassword}
                                    onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                    placeholder="Confirmez votre mot de passe"
                                    className="pr-10"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full"
                            disabled={loading || !formData.password || !formData.confirmPassword}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Création en cours...
                                </>
                            ) : (
                                <>
                                    <Lock className="mr-2 h-4 w-4" />
                                    Créer mon mot de passe
                                </>
                            )}
                        </Button>
                    </form>

                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-2">Conseils pour un mot de passe sécurisé:</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Au moins 8 caractères</li>
                            <li>• Mélange de majuscules et minuscules</li>
                            <li>• Inclure des chiffres</li>
                            <li>• Ajouter des caractères spéciaux (!@#$%^&*)</li>
                        </ul>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default SetPassword;
